import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { formatPlayerBalance } from '@/domain';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { flagUrlForLanguage } from './flag-assets';
import { MenuLanguageDropdown } from './MenuLanguageDropdown';

export interface AuthHeaderProps {
  className?: string;
  loggedIn: boolean;
  /** Override balance label (defaults to CreditsData from the player session). */
  balanceText?: string;
}

export function AuthHeader({ className, loggedIn, balanceText }: AuthHeaderProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { user } = useAuthSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const flagUrl = flagUrlForLanguage(selectedLanguage);
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const displayBalance = balanceText ?? formatPlayerBalance(user);

  useEffect(() => {
    if (!menuOpen) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  return (
    <div ref={rootRef} className={cn('relative flex items-center justify-end gap-3', className)}>
      {loggedIn && (
        <div className="flex flex-col items-end leading-none">
          <span className="text-caption text-text-secondary">
            {isTranslationReady ? t('Balance', { defaultValue: 'Balance' }) : 'Balance'}
          </span>
          <span className="text-label-m tabular-nums text-text-primary">{displayBalance}</span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-expanded={menuOpen}
        aria-label={isTranslationReady ? t('Open account menu', { defaultValue: 'Open account menu' }) : 'Open account menu'}
        className="relative inline-flex size-10 items-center justify-center rounded-lg border border-border-default bg-bg-surface"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden
          className="size-5 text-icon-default"
        >
          <path
            d="M2.5 14.1666H17.5M2.5 9.99992H17.5M2.5 5.83325H17.5"
            stroke="currentColor"
            strokeWidth="1.125"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="absolute top-[11.5px] left-[10px] flex aspect-square size-4 items-center justify-center overflow-hidden rounded-[10px] border-[1.5px] border-bg-surface">
          {flagUrl ? (
            <img
              src={flagUrl}
              alt=""
              className="absolute top-1/2 left-1/2 aspect-[4/3] h-4 w-[21px] shrink-0 -translate-x-1/2 -translate-y-1/2 object-cover"
            />
          ) : null}
        </span>
      </button>

      {menuOpen && (
        <MenuLanguageDropdown
          loggedIn={loggedIn}
          onClose={() => setMenuOpen(false)}
          className="absolute top-[calc(100%+8px)] right-0 z-50"
        />
      )}
    </div>
  );
}
